/* eslint-disable @typescript-eslint/no-unused-vars */
import { useQuery } from "@tanstack/react-query";

import { get } from "lodash";
import { useTranslation } from "react-i18next";

import { client } from "../../bit/board/hooks/useGraphQLClient";
import { useAppContext } from "../../bit/board";

import { QUERY_DIGITAL_RECEPTION } from "../../config/graphql/query";
import { useOfflineContext } from "../../context/Offline";

export const useQueryBoard = () => {
  const { screenId } = useAppContext();
  const [, { language }] = useTranslation();
  const { online, storeData, getData } = useOfflineContext();

  return useQuery(
    ["digitalReception", { id: screenId, language, online }],
    async () => {
      try {
        const data = await client.request<{
          digitalReception: IDigitalReception;
        }>(QUERY_DIGITAL_RECEPTION, {
          id: screenId,
          language,
        });

        await storeData(`/app/config/${language}`, data);
        return data;
      } catch (error) {
        const config = await getData<{ digitalReception: IDigitalReception }>(
          `/app/config/${language}`,
        );
        return config;
      }
    },
    {
      enabled: !!screenId,
      staleTime: 5 * (60 * 1000),
      refetchInterval: 5 * (60 * 1000),
      keepPreviousData: true,
    },
  );
};

export function useClientPlan() {
  const { data } = useQueryBoard();

  return data?.digitalReception?.client?.plan;
}

export function useLayout(): Partial<IDigitalReceptionLayout> {
  const { data, dataUpdatedAt } = useQueryBoard();

  // added to react to re-render when data is updated
  // this needs to be here for the preview to work in the CMS
  // dataUpdatedAt;

  return {
    welcomeMessage: get(
      data,
      `digitalReception.settings.layout.welcomeMessage`,
    ),
    checkin: get(data, "digitalReception.settings.layout.checkin"),
    integratedManualCheckin: get(
      data,
      "digitalReception.settings.layout.integratedManualCheckin",
    ),
    independentManualCheckin: get(
      data,
      "digitalReception.settings.layout.independentManualCheckin",
    ),
    checkout: get(data, "digitalReception.settings.layout.checkout"),
    manualCheckout: get(
      data,
      "digitalReception.settings.layout.manualCheckout",
    ),
    packageDelivery: get(data, "digitalReception.settings.layout.package"),
    foodDelivery: get(data, "digitalReception.settings.layout.food"),
    callReception: get(data, "digitalReception.settings.layout.callReception"),
    overview: get(data, "digitalReception.settings.layout.overview"),
    wifi: get(data, "digitalReception.settings.layout.wifi"),
  };
}

export function useReceptionistPhone() {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.receptionistPhone?.primary;
}

export const useDeliveryReceiver = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.deliveryReceiver;
};

export const useOverview = () => {
  const { data } = useQueryBoard();

  return {
    code: data?.digitalReception?.settings?.overview?.code,
    fields: data?.digitalReception?.settings?.overview?.fields,
  };
};

export const useAnimationsEnabled = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.animations?.enabled;
};

export const useWifiQrCode = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.wifi?.qrCode;
};

export const useClock = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.clock;
};

export const useDigitalReceptionCountry = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.building?.country;
};

export const useIdentificationCode = () => {
  const { integratedManualCheckin, independentManualCheckin } = useLayout();

  let manualCheckoutGuestCode = "";

  if (!!integratedManualCheckin?.enabled) {
    manualCheckoutGuestCode =
      integratedManualCheckin.registration?.fields.find(
        (field) => !!field.manualCheckoutIdentifier,
      )?.code || "";
  }

  if (!!independentManualCheckin?.enabled) {
    manualCheckoutGuestCode =
      independentManualCheckin.registration?.fields.find(
        (field) => !!field.manualCheckoutIdentifier,
      )?.code || "";
  }

  return manualCheckoutGuestCode;
};

export default useQueryBoard;
